import { useState, useMemo } from "react";
import { motion } from "framer-motion";
import { Slider } from "@/components/ui/slider";
import { Users, Tag, TrendingUp, Coins } from "lucide-react";

const fmt = (n) =>
  new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n);

export default function Simulator() {
  const [clients, setClients] = useState([50]);
  const [price, setPrice] = useState([80]);

  const { year1, passivePerYear, passiveTotal, total } = useMemo(() => {
    const c = clients[0];
    const p = price[0];
    const y1 = c * p;
    const passive = c * p * 0.25;
    return {
      year1: y1,
      passivePerYear: passive,
      passiveTotal: passive * 3,
      total: y1 + passive * 3,
    };
  }, [clients, price]);

  return (
    <section id="simulator" className="relative py-28 lg:py-36 px-6 md:px-12 bg-card/30 border-y border-white/5 grain-overlay" data-testid="simulator-section">
      <div className="relative z-10 max-w-6xl mx-auto">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-norton-yellow">
            💰 Simulateur de revenus
          </span>
          <h2 className="text-3xl sm:text-5xl font-heading font-semibold tracking-tight mt-4 leading-tight">
            Estimez vos revenus passifs
          </h2>
          <p className="text-zinc-400 mt-5 text-lg">
            Ajustez les curseurs et découvrez ce que chaque client peut vous rapporter sur 4 ans.
          </p>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="grid lg:grid-cols-2 gap-8 lg:gap-10"
        >
          {/* Controls */}
          <div className="rounded-3xl border border-white/10 bg-background/70 backdrop-blur-xl p-8 lg:p-10">
            <div className="mb-10">
              <div className="flex items-center justify-between mb-5">
                <label className="flex items-center gap-2 text-sm font-medium text-zinc-300">
                  <Users className="h-4 w-4 text-norton-yellow" /> Nombre de clients
                </label>
                <span className="font-heading text-2xl font-bold text-white" data-testid="clients-value">{clients[0]}</span>
              </div>
              <Slider
                value={clients}
                onValueChange={setClients}
                min={1}
                max={500}
                step={1}
                data-testid="calculator-slider-clients"
                className="[&_[role=slider]]:bg-norton-yellow [&_[role=slider]]:border-norton-yellow [&>span:first-child>span]:bg-norton-yellow"
              />
              <div className="flex justify-between text-xs text-zinc-500 mt-3">
                <span>1</span>
                <span>500</span>
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-5">
                <label className="flex items-center gap-2 text-sm font-medium text-zinc-300">
                  <Tag className="h-4 w-4 text-norton-yellow" /> Prix moyen d'une licence
                </label>
                <span className="font-heading text-2xl font-bold text-white" data-testid="price-value">{fmt(price[0])}</span>
              </div>
              <Slider
                value={price}
                onValueChange={setPrice}
                min={20}
                max={300}
                step={5}
                data-testid="calculator-slider-price"
                className="[&_[role=slider]]:bg-norton-yellow [&_[role=slider]]:border-norton-yellow [&>span:first-child>span]:bg-norton-yellow"
              />
              <div className="flex justify-between text-xs text-zinc-500 mt-3">
                <span>20 €</span>
                <span>300 €</span>
              </div>
            </div>
          </div>

          {/* Output */}
          <div className="rounded-3xl border border-norton-yellow/30 bg-gradient-to-br from-norton-yellow/[0.07] to-transparent p-8 lg:p-10 flex flex-col justify-between glow-yellow">
            <div className="space-y-5">
              <div className="flex items-center justify-between rounded-xl border border-white/10 bg-background/40 p-5">
                <div className="flex items-center gap-3">
                  <TrendingUp className="h-5 w-5 text-zinc-400" />
                  <span className="text-sm text-zinc-300">Revenus Année 1 (vente)</span>
                </div>
                <span className="font-heading text-xl font-bold text-white" data-testid="result-year1">{fmt(year1)}</span>
              </div>

              <div className="flex items-center justify-between rounded-xl border border-white/10 bg-background/40 p-5">
                <div className="flex items-center gap-3">
                  <Coins className="h-5 w-5 text-zinc-400" />
                  <span className="text-sm text-zinc-300">Revenu passif / an (×25%)</span>
                </div>
                <span className="font-heading text-xl font-bold text-white" data-testid="result-passive-year">{fmt(passivePerYear)}</span>
              </div>

              <div className="flex items-center justify-between rounded-xl border border-white/10 bg-background/40 p-5">
                <div className="flex items-center gap-3">
                  <Coins className="h-5 w-5 text-norton-yellow" />
                  <span className="text-sm text-zinc-300">Revenus passifs (Années 2, 3, 4)</span>
                </div>
                <span className="font-heading text-xl font-bold text-norton-yellow" data-testid="result-passive-total">{fmt(passiveTotal)}</span>
              </div>
            </div>

            <div className="mt-8 border-t border-white/10 pt-6">
              <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-1">Total sur 4 ans</div>
              <div className="font-heading text-5xl sm:text-6xl font-bold text-norton-yellow tracking-tight" data-testid="result-total">
                {fmt(total)}
              </div>
              <p className="text-xs text-zinc-500 mt-3">
                Estimation indicative. Vous vendez une fois, Norton gère le reste.
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
